import React, { useContext, useEffect } from 'react'
import { Button } from '@fluentui/react-components'
import { useForm } from 'react-hook-form'
import { ConsequenceRiskInfo } from './ConsequenceRiskInfo'
import { ConsequenceRiskProbabilityInfo } from './RiskConsequenceProbabilityInfo'
import { t } from 'i18next'
import { RiskAnalysis, ConsequenceAndProbabilityRisk, RiskAnalysisActionPlanResponse } from '../../../../../api/schemas/schema'
import { DataContext } from '../../../../../context/DataContext'
import { api } from '../../../../../helpers/apiHelper'
import { handleMessage } from '../../../../../helpers/stateHelper'
import { IItemState } from '../../../../../interfaces/IItemState'
import { Grid } from '../../../../common/grid/Grid'
import { GridItem } from '../../../../common/grid/GridItem'
import { ControlledCheckbox } from '../../../../common/inputs/ControlledCheckbox'
import { ControlledMoneyInput } from '../../../../common/inputs/ControlledMoneyInput'
import { ControlledTextInput } from '../../../../common/inputs/TextInput'
import { DraggableModal } from '../../../../common/modal/DraggableModal'
import { ModalActions } from '../../../../common/modal/Modal'
import { Form } from '../../../../common/form/Form'

type ModalConsequenceEvaluateProps = {
   setRiskAnalysisState: React.Dispatch<React.SetStateAction<IItemState<RiskAnalysis>>>
   analysis: RiskAnalysis
   open: boolean
   checkboxHasLocalState: boolean
   close: () => void
   toggleCheckbox: (risk: ConsequenceAndProbabilityRisk, openModal?: boolean) => ConsequenceAndProbabilityRisk
   selectedRisk: ConsequenceAndProbabilityRisk
   disabled?: boolean
   manualIsDirty: boolean
}

export const ModalConsequenceEvaluate = ({
   setRiskAnalysisState,
   analysis,
   open,
   checkboxHasLocalState,
   close,
   toggleCheckbox,
   selectedRisk,
   disabled,
   manualIsDirty,
}: ModalConsequenceEvaluateProps) => {
   const {
      control,
      reset,
      handleSubmit,
      watch,
      setValue,
      formState: { isDirty, isSubmitting },
   } = useForm<ConsequenceAndProbabilityRisk>()
   const dataContext = useContext(DataContext)
   const setRootState = dataContext.setRootState
   const { userAccount } = dataContext.state

   useEffect(() => {
      if (selectedRisk) {
         reset(selectedRisk)
      }
   }, [selectedRisk, reset])

   const onSubmit = async (data: ConsequenceAndProbabilityRisk) => {
      try {
         data.modifiedBy = userAccount
         data.modifiedById = userAccount.id

         if (data.actionRequired === false) {
            data.proposedAction = null
            data.estimatedCost = null
         }

         const response: RiskAnalysisActionPlanResponse = (
            await api().createOrUpdateActionPlanForConsequenceAndProbabilityRisk(analysis.organisationId, analysis.id, data)
         ).data

         const updatedRiskAnalysis = response.updatedAnalysis
         const updatedRisk = updatedRiskAnalysis.consequenceAndProbabilityRisks.find((r) => r.id === data.id)
         const createdActionPlan = response.actionPlan
         if (createdActionPlan == null) {
            updatedRiskAnalysis.actionPlans = updatedRiskAnalysis.actionPlans.filter((ap) => ap.riskId !== updatedRisk.id)
         }

         setRiskAnalysisState((prev) => ({
            ...prev,
            item: {
               ...prev.item,
               ...updatedRiskAnalysis,
            },
         }))

         close()
         reset(updatedRisk)
         handleMessage(setRootState, 'success', t('ItemSuccessfullyUpdated', { Item: t('Risk') }))
      } catch (error) {
         handleMessage(setRootState, 'error', t('CouldNotUpdateItem', { Item: t('Risk') }))
      }
   }

   const onClose = () => {
      if (checkboxHasLocalState) {
         toggleCheckbox(selectedRisk)
      }
      reset()
      close()
   }

   return (
      <DraggableModal open={open} title={t('AnalyseEvaluate')} onClose={onClose}>
         <Form onSubmit={handleSubmit(onSubmit)}>
            <Grid>
               <ConsequenceRiskInfo selectedRisk={selectedRisk} />
               <ConsequenceRiskProbabilityInfo selectedRisk={selectedRisk} />
               <GridItem size="12/12">
                  {watch('actionRequired') !== undefined && (
                     <ControlledCheckbox
                        onChange={(e, data) => {
                           setValue('actionRequired', Boolean(data.checked), { shouldDirty: true })
                           if (!data.checked) {
                              setValue('proposedAction', null)
                              setValue('estimatedCost', null)
                           }
                        }}
                        label={t('CreateProposedAction')}
                        control={control}
                        name={'actionRequired'}
                        checked={watch('actionRequired')}
                        disabled={disabled}
                     />
                  )}
               </GridItem>
               {watch('actionRequired') && (
                  <>
                     <GridItem size="12/12">
                        <ControlledTextInput control={control} name={'proposedAction'} label={t('ProposedAction')} multiline required disabled={disabled} />
                     </GridItem>
                     <GridItem size="12/12">
                        <ControlledMoneyInput control={control} name={'estimatedCost'} label={t('EstimatedCost')} disabled={disabled} />
                     </GridItem>
                  </>
               )}
               <GridItem size="12/12">
                  <ControlledTextInput control={control} name={'comment'} label={t('Comment')} multiline disabled={disabled} />
               </GridItem>
               <GridItem size="12/12">
                  <ModalActions>
                     <Button onClick={onClose} disabled={isSubmitting}>
                        {disabled ? t('Close') : t('Cancel')}
                     </Button>
                     {!disabled && (
                        <Button type="submit" appearance="primary" disabled={(!manualIsDirty && !isDirty) || isSubmitting}>
                           {t('Save')}
                        </Button>
                     )}
                  </ModalActions>
               </GridItem>
            </Grid>
         </Form>
      </DraggableModal>
   )
}
